import React, { type FC, useMemo } from 'react';
import { useEnvironment, useTranslation } from '@wix/yoshi-flow-editor';
import { Text, TextTypography } from 'wix-ui-tpa/cssVars';
import { DataHooks, AD_MOBILE_WIDTH, AD_MOBILE_HEIGHT } from '@app/constants';
import { useLimitResize, useWidgetSelector } from '@app/hooks';
import {
  selectAdUnit,
  selectNotificationType,
} from '@app/store/widget/slices/widget-slice';
import { ADUNIT_SIZE } from '@app/types';
import { getDimensionsFromSize, getSizeTypeFromSize } from '@app/utils';
import { classes } from '../Widget.st.css';
import AdsenseNotification from './adsense-notification';

const EditorContent: FC = () => {
  const { t } = useTranslation();
  const notificationType = useWidgetSelector(selectNotificationType);
  const adUnit = useWidgetSelector(selectAdUnit);
  const { isMobile } = useEnvironment();

  const sizeType = useMemo(
    () => getSizeTypeFromSize(adUnit?.contentAdsSettings?.size),
    [adUnit],
  );

  const fixedDimensions = useMemo(
    () => getDimensionsFromSize(adUnit?.contentAdsSettings?.size),
    [adUnit],
  );

  // Use ResizeObserver to detect and limit widget resize
  const containerRef = useLimitResize({
    sizeType,
    fixedDimensions,
  });

  const widgetSize = useMemo(() => {
    if (isMobile) {
      return {
        width: AD_MOBILE_WIDTH,
        height: AD_MOBILE_HEIGHT,
      };
    }

    return sizeType === ADUNIT_SIZE.FIXED
      ? {
          width: fixedDimensions.width,
          height: fixedDimensions.height,
        }
      : {};
  }, [sizeType, fixedDimensions, isMobile]);

  return (
    <div ref={containerRef} className={classes.WidgetRoot} style={widgetSize}>
      <div className={classes.Wrapper}>
        <div className={classes.Header}>
          <div className={classes.LogoAndTitleWrapper}>
            <div className={classes.Logo}></div>
            <div>
              <Text
                tagName="h2"
                className={classes.TitleHeader}
                data-hook={DataHooks.adsenseTitle}
              >
                {t('adsense.widget.header.title')}
              </Text>
            </div>
          </div>
          <Text
            className={classes.TextHeader}
            typography={TextTypography.runningText}
          >
            {t(`adsense.widget.header.text.${notificationType}`)}
          </Text>
        </div>
        <AdsenseNotification />
      </div>
    </div>
  );
};

export default EditorContent;
